import { Component, OnInit, OnDestroy, inject, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { TranslocoModule } from '@jsverse/transloco';
import { DashboardSidebarComponent } from '../sidebar/dashboard-sidebar.component';
import { DashboardTopbarComponent } from '../topbar/dashboard-topbar.component';
import { AppFooterComponent } from '@app/shared/components/app-footer/app-footer.component';

@Component({
  standalone: true,
  selector: 'app-dashboard-layout',
  templateUrl: './dashboard-layout.component.html',
  styleUrl: './dashboard-layout.component.scss',
  imports: [
    CommonModule,
    RouterModule,
    DrawerModule,
    ButtonModule,
    TranslocoModule,
    DashboardSidebarComponent,
    DashboardTopbarComponent,
    AppFooterComponent
  ]
})
export class DashboardLayoutComponent implements OnInit, OnDestroy {
  private readonly ngZone = inject(NgZone);

  sidebarVisible = false;
  sidebarCollapsed = false;
  isMobile = false;

  private resizeHandler = () => this.checkScreenSize();
  private viewportResizeHandler: (() => void) | null = null;

  ngOnInit() {
    this.checkScreenSize();
    window.addEventListener('resize', this.resizeHandler);
    this.setupViewportHeightTracking();
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.resizeHandler);
    this.cleanupViewportHeightTracking();
  }

  private checkScreenSize(): void {
    this.isMobile = window.innerWidth < 992;
    if (!this.isMobile) {
      this.sidebarVisible = false;
    }
  }

  toggleSidebar(): void {
    this.sidebarVisible = !this.sidebarVisible;
  }

  toggleSidebarCollapse(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  onSidebarHide(): void {
    this.sidebarVisible = false;
  }

  /**
   * Track visualViewport height and expose it as a CSS custom property.
   * When virtual keyboard opens, visualViewport.height shrinks —
   * we use this to constrain .page-content so it shrinks instead of scrolling.
   */
  private setupViewportHeightTracking(): void {
    const vv = window.visualViewport;
    if (!vv) return;

    this.ngZone.runOutsideAngular(() => {
      const update = () => {
        document.documentElement.style.setProperty(
          '--visual-viewport-height',
          `${vv.height}px`,
        );
      };

      update();
      this.viewportResizeHandler = update;
      vv.addEventListener('resize', update);
    });
  }

  private cleanupViewportHeightTracking(): void {
    if (this.viewportResizeHandler && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.viewportResizeHandler);
      this.viewportResizeHandler = null;
    }
  }
}
